/**
 * generatePage/studioStyled.ts
 * Qué hace: armazón de la pantalla Generate como "studio" de dos zonas —
 *   canvas de trabajo (izquierda, fluido) + rail de control (derecha, fijo y
 *   pegajoso). CSS puro, sin lógica.
 * Qué exporta: Studio*, Canvas*, Rail*, Filter*, Search*.
 * Quién lo usa: pages/GeneratePage.tsx.
 *
 * Por qué el rail: antes Novah era una banda horizontal ARRIBA que desaparecía
 * al terminar (novahDone) y volvía con un botón "Generate more". El asistente
 * es la herramienta central de la pantalla, así que ahora es permanente y el
 * canvas queda libre para lo único que importa mirar: los posts.
 */
import styled, { keyframes, css } from 'styled-components';

const RAIL_WIDTH = '392px';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const StudioRoot = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${RAIL_WIDTH};
  gap: 0;
  align-items: start;
  min-height: calc(100vh - 64px);

  /* Bajo desktop el rail deja de ser columna y pasa arriba del canvas: en un
     tablet/mobile una columna de 392px se come la pantalla. */
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* ── Canvas (izquierda) ── */
export const Canvas = styled.div`
  min-width: 0;
  padding: 0 24px 48px;
  display: flex;
  flex-direction: column;
`;

export const CanvasHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 0 14px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 0.4s ease-out both;
`;

export const CanvasTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

export const CanvasSub = styled.p`
  margin: 2px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

export const HeaderSpacer = styled.div`
  margin-left: auto;
  display: flex;
  gap: 10px;
`;

/* Barra de trabajo: filtros + búsqueda. Pegajosa porque con 80 posts en la
   grilla, tener que subir hasta arriba para cambiar de red es una fricción que
   se paga en cada uso. */
export const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 0 12px;
  margin-bottom: 4px;
  background: ${({ theme }) => theme.colors.background};
  /* Difuminado inferior en vez de borde: el borde corta la grilla en seco. */
  box-shadow: 0 10px 12px -10px rgba(0, 0, 0, 0.16);
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const FilterChip = styled.button<{ $active: boolean }>`
  height: 32px;
  padding: 0 12px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  ${({ $active, theme }) => ($active
    ? css`
        border: 1px solid transparent;
        background: ${theme.colors.primary};
        color: #fff;
      `
    : css`
        border: 1px solid ${theme.colors.border};
        background: ${theme.colors.surface};
        color: ${theme.colors.textSecondary};

        &:hover {
          border-color: ${theme.colors.borderHover};
          color: ${theme.colors.text};
        }
      `)}

  &:active { transform: scale(0.96); }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const FilterCount = styled.span<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-variant-numeric: tabular-nums;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $active, theme }) => ($active ? 'rgba(255,255,255,0.22)' : theme.colors.surfaceHover)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textMuted)};
`;

export const SearchBox = styled.div`
  position: relative;
  margin-left: auto;
  flex: 0 1 260px;
  min-width: 180px;
`;

export const SearchField = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 32px 0 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }

  /* Chrome pinta su propia X en los type=search: quedarían dos. */
  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}26;
  }
`;

export const SearchGlyphSlot = styled.span`
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: none;
`;

export const SearchClearBtn = styled.button`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  /* Visible 22px, área de toque 40×40. */
  &::after {
    content: '';
    position: absolute;
    inset: -9px;
  }
`;

export const NoMatches = styled.div`
  padding: 48px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

/* ── Rail (derecha) ── */
export const Rail = styled.aside`
  position: sticky;
  top: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${({ theme }) => theme.colors.surface};
  border-left: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    position: static;
    height: auto;
    border-left: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    /* En una sola columna el rail va ARRIBA: si queda debajo de la grilla, en
       mobile hay que scrollear toda la tanda para llegar al chat, que es la
       acción principal de la pantalla. */
    order: -1;
  }
`;

export const RailHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

export const RailAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.surfaceHover};

  img { width: 100%; height: 100%; object-fit: cover; }
`;

export const RailTitle = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text};
    line-height: 1.25;
  }

  span {
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

/* El chat scrollea solo dentro del rail; el canvas scrollea aparte. */
export const RailScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    overflow: visible;
  }
`;

/* Progreso del stream: vive en el rail, no en medio de la grilla — así el
   usuario ve llenarse el canvas mientras el estado se lee siempre en el mismo
   lugar. */
export const RailProgress = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
`;

export const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 7px;
  font-variant-numeric: tabular-nums;
`;

export const ProgressTrack = styled.div`
  height: 5px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.surfaceHover};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => Math.min(100, Math.max(0, $progress))}%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.35s ease;
`;

/* ── Ajustes avanzados (colapsable dentro del rail) ── */
/* Toggle y panel viven DENTRO del área que scrollea, no como hermanos del rail:
   como hermanos competían por el alto de 100vh y el panel terminaba empujado
   fuera de la pantalla, aplastado a 29px. */
export const AdvancedToggle = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 14px;
  padding: 11px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $open }) => ($open ? theme.colors.surfaceHover : 'transparent')};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover { color: ${({ theme }) => theme.colors.text}; }

  svg {
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
  }
`;

export const AdvancedPanel = styled.div`
  margin-top: 10px;
  padding: 14px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.background};
`;
